import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { cn } from "@/shared/lib";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";

import { ACTIVITY_PERIODS, type ActivityPeriod } from "../../model/ACTIVITY_PERIODS";
import { localDate } from "../../model/localDate";
import type { PlayerSummary } from "../../model/PlayerSummary";
import { ProfileSection } from "./ProfileSection";

// Сколько последних месяцев в периоде «Год».
const YEAR_MONTHS = 12;

const TOTAL = msg({ message: "{count, plural, one {# match} other {# matches}}" });
const SUMMARY = msg({
  message: "Matches from {from} to {to}: {total}, most — {peak} ({peakLabel})",
});

type Bar = { key: string; label: string; matches: number };

type ActivityProps = {
  // Матчи по месяцам от первого матча до текущего и по дням за последние 30 дней.
  months: PlayerSummary["monthly_matches"];
  days: PlayerSummary["daily_matches"];
  period: ActivityPeriod;
  onPeriodChange: (period: ActivityPeriod) => void;
};

// «Активность»: матчи за 30 дней по дням, за год или за всё время по месяцам; последний
// столбец (сегодня или текущий месяц) — акцентом. Истории нет — блока нет.
export const Activity = ({ months, days, period, onPeriodChange }: ActivityProps) => {
  const { i18n, t } = useLingui();
  if (months.length === 0) {
    return null;
  }
  const dayFormat = new Intl.DateTimeFormat(i18n.locale, { day: "numeric", month: "short" });
  const monthFormat = new Intl.DateTimeFormat(i18n.locale, { month: "short", year: "numeric" });
  const bars: Bar[] =
    period === "30d"
      ? days.map((day) => ({
          key: day.day,
          label: dayFormat.format(localDate(day.day)),
          matches: day.matches,
        }))
      : (period === "year" ? months.slice(-YEAR_MONTHS) : months).map((month) => ({
          key: month.month,
          label: monthFormat.format(localDate(month.month)),
          matches: month.matches,
        }));
  const total = bars.reduce((sum, bar) => sum + bar.matches, 0);
  const first = bars[0];
  const last = bars.at(-1);
  const peak = bars.reduce<Bar | undefined>(
    (best, bar) => (best === undefined || bar.matches > best.matches ? bar : best),
    undefined,
  );
  const labels: Record<ActivityPeriod, string> = {
    "30d": t`30 days`,
    year: t`Year`,
    all: t`All time`,
  };

  return (
    <ProfileSection
      title={<Trans>Activity</Trans>}
      aside={
        <ToggleGroup
          type="single"
          aria-label={t`Period`}
          value={period}
          onValueChange={(value) => {
            const next = ACTIVITY_PERIODS.find((option) => option === value);
            if (next !== undefined) {
              onPeriodChange(next);
            }
          }}
        >
          {ACTIVITY_PERIODS.map((option) => (
            <ToggleGroupItem key={option} value={option}>
              {labels[option]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      }
    >
      <p className="font-mono text-[15px] font-semibold text-accent">
        {i18n._({ ...TOTAL, values: { count: total } })}
      </p>
      {first === undefined || last === undefined || peak === undefined ? (
        <p className="text-sm text-muted">
          <Trans>Matches by day will show up after your next match.</Trans>
        </p>
      ) : (
        <>
          <div
            role="img"
            aria-label={i18n._({
              ...SUMMARY,
              values: {
                from: first.label,
                to: last.label,
                total,
                peak: peak.matches,
                peakLabel: peak.label,
              },
            })}
            className="flex h-[150px] items-end gap-[3px] border-b border-border"
          >
            {bars.map((bar) => (
              <span
                key={bar.key}
                title={`${bar.label}: ${String(bar.matches)}`}
                className={cn(
                  "min-w-0 flex-1 rounded-t-[3px]",
                  bar.matches === 0
                    ? "bg-surface-raised"
                    : bar === last
                      ? "bg-accent"
                      : "bg-neutral",
                )}
                style={{
                  height:
                    bar.matches === 0
                      ? 1
                      : `${String(Math.max(3, (bar.matches / peak.matches) * 100))}%`,
                }}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-subtle">
            <span>{first.label}</span>
            <span>{last.label}</span>
          </div>
        </>
      )}
    </ProfileSection>
  );
};
